import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Tooltip, notification } from 'antd';
import { CaretDownIcon, CaretRightIcon, CheckCircleIcon, WarningCircleIcon, XCircleIcon } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../store/store';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import designServices from '../../../../services/oneButtonServices/designServices';
import packingSlipService from '../../../../services/packingSlipService';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { EligibilityDetailsContext, EligibilityDetailsHost } from './EligibilityDetailsPopup';
import {
    ELIGIBILITY_ROWS,
    INSTALLATION_TYPE_ROWS,
    PROPERTY_TYPE_ROWS,
    SYSTEM_SPEC_ROWS,
    evaluateFinanceEligibility,
    findFinanceEligibilityPartner,
    getEligibilityRowInfo,
    getOverallEligibilityReasonRows,
    getOverallEligibilityStatus,
    isFinanceEligibilityValuePartner,
    scoreFinanceEligibilityPartner,
    formatEligibilityCurrentValue,
    type EligibilityCurrentValues,
    type EligibilityReasonRow,
    type EligibilityStatus,
    type FinanceEligibilityCatalog,
    type FinanceEligibilityPartner,
    type FinanceEligibilityScore,
} from '../../../../services/financeEligibilityService';
import {
    PRODUCT_DISPLAY_ORDER,
    buildOpportunityProductSavePayload,
    isLightreach,
    matchProductName,
    rowFromApi,
} from '../oneBUTTONDashboard/productsFinanceHubHelpers';
import {
    evaluateDesignLossesMismatchForHubSave,
    showDesignLossesMismatchModal,
} from '../oneBUTTONDashboard/designLossesFinanceGate';
import type { HubGridRow } from '../../../../@types/oneButtonType';

type PartnerColumn = {
    partner: FinanceEligibilityPartner;
    statuses: Record<string, EligibilityStatus>;
    overall: EligibilityStatus;
    reasonRows: EligibilityReasonRow[];
    score: FinanceEligibilityScore;
    eligibleCount: number;
    energyCommunityWeight: number;
};

function overallEligibilityLabel(status: EligibilityStatus): string {
    if (status === 'success') return 'Eligible';
    if (status === 'failed') return 'Not eligible';
    return 'Review';
}

const OVERALL_STATUS_RANK: Record<EligibilityStatus, number> = {
    success: 0,
    warning: 1,
    failed: 2,
};

function countEligibleRows(statuses: Record<string, EligibilityStatus>): number {
    return ELIGIBILITY_ROWS.filter((row) => statuses[row.key] === 'success').length;
}

/** Extra rank when Energy Community is a pass — Enfin on an EC site outranks an equal count. */
function energyCommunityWeight(statuses: Record<string, EligibilityStatus>): number {
    return statuses.energyCommunity === 'success' ? 1 : 0;
}

/** Best first: more passing items, then Energy Community weight, then overall status. */
function compareColumns(a: PartnerColumn, b: PartnerColumn): number {
    return (
        b.eligibleCount - a.eligibleCount ||
        b.energyCommunityWeight - a.energyCommunityWeight ||
        OVERALL_STATUS_RANK[a.overall] - OVERALL_STATUS_RANK[b.overall]
    );
}

/** Confirms a save onto a partner the current design fails, listing the broken rules. */
function confirmIneligiblePartner(column: PartnerColumn, theme: string): Promise<boolean> {
    const failedRows = column.reasonRows.filter((row) => row.status === 'failed');
    return new Promise((resolve) => {
        Modal.confirm({
            title: `${column.partner.displayName} is not eligible`,
            content: (
                <div className="fp-eligibility-change-modal__confirm">
                    {failedRows.length ? (
                        <>
                            <p>This design does not meet the following {column.partner.displayName} requirements:</p>
                            <ul>
                                {failedRows.map((row) => (
                                    <li key={row.key}>
                                        <strong>{row.group ? `${row.group} — ${row.label}` : row.label}</strong>
                                        {row.reason ? `: ${row.reason}` : ''}
                                    </li>
                                ))}
                            </ul>
                        </>
                    ) : (
                        <p>This design does not meet the {column.partner.displayName} eligibility rules.</p>
                    )}
                    <p>Save this finance partner anyway?</p>
                </div>
            ),
            okText: 'Save anyway',
            cancelText: 'Cancel',
            centered: true,
            zIndex: 14000,
            className: `obd-theme-${theme}`,
            rootClassName: `obd-theme-${theme}`,
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });
}

function StatusMark({
    status,
    reason,
    reasonRows,
    showLabel,
}: {
    status: EligibilityStatus;
    reason?: string;
    reasonRows?: EligibilityReasonRow[];
    showLabel?: boolean;
}) {
    const icon =
        status === 'success' ? (
            <CheckCircleIcon
                size={18}
                weight="fill"
                className="fp-eligibility__icon fp-eligibility__icon--success"
                aria-label="Eligible"
            />
        ) : status === 'failed' ? (
            <XCircleIcon
                size={18}
                weight="fill"
                className="fp-eligibility__icon fp-eligibility__icon--failed"
                aria-label="Not eligible"
            />
        ) : (
            <WarningCircleIcon
                size={18}
                weight="fill"
                className="fp-eligibility__icon fp-eligibility__icon--warning"
                aria-label="Review"
            />
        );
    const showDetails = React.useContext(EligibilityDetailsContext);
    const heading = status === 'failed' ? 'Not eligible' : 'Review';
    const hasDetails =
        (status === 'failed' || status === 'warning') && Boolean(reason || reasonRows?.length);
    const openDetails = () => {
        if (hasDetails && showDetails) showDetails({ heading, rows: reasonRows, reason });
    };
    const mark = (
        <span
            className={`fp-eligibility__icon-wrap${hasDetails ? ' fp-eligibility__icon-wrap--help' : ''}`}
            role={hasDetails && showDetails ? 'button' : undefined}
            tabIndex={hasDetails && showDetails ? 0 : undefined}
            onClick={(event) => {
                if (!hasDetails || !showDetails) return;
                event.stopPropagation();
                openDetails();
            }}
            onKeyDown={(event) => {
                if (!hasDetails || !showDetails) return;
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                event.stopPropagation();
                openDetails();
            }}
            onMouseDown={(event) => {
                if (hasDetails && showDetails) event.stopPropagation();
            }}
        >
            {icon}
            {showLabel ? (
                <span className={`fp-eligibility__overall-label fp-eligibility__overall-label--${status}`}>
                    {overallEligibilityLabel(status)}
                </span>
            ) : null}
        </span>
    );

    if (!hasDetails) {
        return showLabel ? mark : icon;
    }

    return (
        <Tooltip
            title={reasonRows?.length ? `${heading} — click for details` : reason}
            overlayClassName="fp-eligibility-tooltip"
            placement="top"
            mouseEnterDelay={0.15}
            zIndex={13000}
        >
            {mark}
        </Tooltip>
    );
}

function resolvePartnerDirectory(
    displayName: string,
    financePartnersList: Array<{ Text?: string; Value?: string | number }> | undefined,
): { id: number; code: string; display: string } {
    const name = displayName.trim().toLowerCase();
    const match = (financePartnersList ?? []).find(
        (item) => String(item.Text ?? '').trim().toLowerCase() === name,
    );
    if (match) {
        const text = String(match.Text ?? displayName);
        return { id: Number(match.Value) || 0, code: text, display: text };
    }
    return { id: 0, code: displayName, display: displayName };
}

type FinancePartnerChangeModalProps = {
    open: boolean;
    onClose: () => void;
    catalog: FinanceEligibilityCatalog;
    currentValues: EligibilityCurrentValues;
    currentPartnerName: string;
};

const FinancePartnerChangeModal: React.FC<FinancePartnerChangeModalProps> = ({
    open,
    onClose,
    catalog,
    currentValues,
    currentPartnerName,
}) => {
    const theme = useOneButtonStore((s) => s.theme);
    const opportunityDetails = useOneButtonStore((s: any) => s.opportunityDetails);
    const financePartnersList = useOneButtonStore((s) => s.financePartnersList) as Array<{
        Text?: string;
        Value?: string | number;
    }>;
    const productTypeList = useOneButtonStore((s) => s.productTypeList) as Array<{
        Text: string;
        Value?: string | number;
    }>;
    const setOpportunityDetails = useOneButtonStore((s) => s.setOpportunityDetails);
    const setFinanceSelectionRefreshTrigger = useOneButtonStore((s) => s.setFinanceSelectionRefreshTrigger);
    const setSolarUtilityData = useOneButtonStore((s) => s.setSolarUtilityData);

    const currentPartnerId = findFinanceEligibilityPartner(currentPartnerName, catalog)?.id ?? '';
    const [selectedId, setSelectedId] = useState(currentPartnerId);
    const [saving, setSaving] = useState(false);
    const [openRows, setOpenRows] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (open) {
            setSelectedId(currentPartnerId);
            setOpenRows({});
        }
    }, [open, currentPartnerId]);

    const columns = useMemo<PartnerColumn[]>(() => {
        return (catalog.partners ?? [])
            .filter((partner) => isFinanceEligibilityValuePartner(partner))
            .map((partner) => {
                const statuses = evaluateFinanceEligibility(partner.displayName, currentValues, catalog);
                return {
                    partner,
                    statuses,
                    overall: getOverallEligibilityStatus(statuses),
                    reasonRows: getOverallEligibilityReasonRows(partner, currentValues, statuses),
                    score: scoreFinanceEligibilityPartner(statuses, currentValues),
                    eligibleCount: countEligibleRows(statuses),
                    energyCommunityWeight: energyCommunityWeight(statuses),
                };
            })
            .sort(compareColumns);
    }, [catalog, currentValues]);

    /** Highest passing-item count; skip partners that failed outright. */
    const recommendedId = useMemo(() => {
        const candidate = columns.find((column) => column.overall !== 'failed');
        return candidate && candidate.eligibleCount > 0 ? candidate.partner.id : '';
    }, [columns]);

    const selectedColumn = columns.find((column) => column.partner.id === selectedId) ?? null;
    const canSave = Boolean(selectedColumn && selectedId && selectedId !== currentPartnerId);

    const handleSave = async () => {
        if (!selectedColumn || !canSave) return;
        if (selectedColumn.overall === 'failed' && !(await confirmIneligiblePartner(selectedColumn, theme))) {
            return;
        }
        const opportunityId = Number(
            opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId ?? 0,
        );
        const sfId = String(
            sessionStorage.getItem('SalesForceId') ?? opportunityDetails?.SalesForce_Id ?? '',
        ).trim();
        if (!opportunityId || !sfId) {
            notification.error({
                message: 'Error',
                description: 'Opportunity data is not loaded.',
                placement: 'topRight',
            });
            return;
        }

        const originalRaw =
            opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        const originalArr = Array.isArray(originalRaw) ? originalRaw : [];
        const ids = resolvePartnerDirectory(selectedColumn.partner.displayName, financePartnersList);
        const productNames = Array.from(
            new Set([
                ...PRODUCT_DISPLAY_ORDER,
                ...originalArr.map((item: any) =>
                    String(item?.Product_Type_Name ?? item?.product_Type_Name ?? '').trim(),
                ),
            ]),
        ).filter(Boolean);

        const rows: HubGridRow[] = productNames.map((name) => {
            const found = originalArr.find((item: any) => matchProductName(item, name));
            const base = rowFromApi(found, name);
            if (name === 'Solar') {
                return {
                    ...base,
                    activeFlag: true,
                    finance_Partner_Display_Name: ids.display,
                    finance_Partner_Id: ids.id,
                    finance_Partner: ids.code || ids.display,
                    finance_Method_Name: '',
                    methodOptions: [],
                    methodsLoading: false,
                };
            }
            if (!isLightreach(ids.display) && isLightreach(base.finance_Partner_Display_Name)) {
                return {
                    ...base,
                    finance_Partner_Display_Name: 'Select Finance Partner',
                    finance_Partner_Id: 0,
                    finance_Partner: '',
                    finance_Method_Name: '',
                    methodOptions: [],
                    methodsLoading: false,
                };
            }
            return { ...base, methodOptions: [], methodsLoading: false };
        });

        setSaving(true);
        try {
            const losses = await designServices.SystemDesignLosses(String(opportunityId));
            const mismatch = evaluateDesignLossesMismatchForHubSave(rows, losses?.LossesSource);
            if (mismatch.blocked) {
                showDesignLossesMismatchModal(
                    mismatch.message ?? 'Design losses do not match the selected finance partner.',
                    theme,
                );
                return;
            }

            const opportunityProduct = buildOpportunityProductSavePayload(
                rows,
                originalArr,
                opportunityId,
                productTypeList ?? [],
                'Solar',
            );
            const response = await opportunityInfoServices.updateOpportunityProduct({
                opportunityId,
                salesForce_Id: sfId,
                opportunityProduct,
            });
            notification.success({
                message: 'Success',
                description: response?.Message || response?.message || 'Solar finance partner saved.',
                placement: 'topRight',
            });

            const refreshed = await opportunityInfoServices.getOpportunityDetail(sfId);
            await quoteServices.contractGetLoanStatusByOpportunity(opportunityId);
            const detailData = refreshed?.data ?? refreshed;
            if (detailData) setOpportunityDetails(detailData);
            setFinanceSelectionRefreshTrigger(Date.now());

            packingSlipService.fetchFinancePartnerDetails().then((details) => {
                useViewerStore.setState({ financePartnerDetails: details });
            });

            try {
                const utilityResult = await designServices.RetrieveUtilityInformation(opportunityId);
                setSolarUtilityData(utilityResult?.utilityDetails ?? null);
            } catch {
                setSolarUtilityData(null);
            }
            onClose();
        } catch (error: any) {
            notification.error({
                message: 'Error',
                description:
                    error?.response?.data?.Message || error?.message || 'Failed to save finance partner.',
                placement: 'topRight',
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title={
                <div className="fp-eligibility-change-modal__title">
                    <strong>Compare Partners</strong>
                    <span>Partners for this design. Recommended is highlighted.</span>
                </div>
            }
            width={920}
            zIndex={12000}
            centered
            destroyOnClose
            getContainer={() => document.body}
            className="fp-eligibility-change-modal"
            footer={null}
        >
            <EligibilityDetailsHost>
                <div className="fp-eligibility-change-modal__table-wrap">
                    <table className="fp-eligibility-change-modal__table">
                        <thead>
                            <tr>
                                <th>Rule</th>
                                <th className="fp-eligibility-change-modal__current">This design</th>
                                {columns.map((column) => {
                                    const isCurrent = column.partner.id === currentPartnerId;
                                    const isRecommended = column.partner.id === recommendedId;
                                    return (
                                        <th
                                            key={column.partner.id}
                                            className={`fp-eligibility-change-modal__partner${isCurrent ? ' is-current' : ''}${isRecommended ? ' is-recommended' : ''}`}
                                        >
                                            <span className="fp-eligibility-change-modal__partner-name">
                                                {column.partner.displayName}
                                            </span>
                                            <span className="fp-eligibility-change-modal__partner-review">
                                                <StatusMark
                                                    status={column.overall}
                                                    reasonRows={column.reasonRows}
                                                    showLabel
                                                />
                                            </span>
                                            {isRecommended ? (
                                                <span className="fp-eligibility-change-modal__col-tag">Recommended</span>
                                            ) : null}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {ELIGIBILITY_ROWS.map((row) => {
                                const nestedRows =
                                    row.key === 'propertyType'
                                        ? PROPERTY_TYPE_ROWS
                                        : row.key === 'installationType'
                                          ? INSTALLATION_TYPE_ROWS
                                          : row.key === 'systemSpecs'
                                            ? SYSTEM_SPEC_ROWS
                                            : null;
                                const nestedOpen = Boolean(openRows[row.key]);
                                return (
                                    <React.Fragment key={row.key}>
                                        <tr className={nestedRows ? 'fp-eligibility-change-modal__row--expandable' : undefined}>
                                            <td
                                                onClick={
                                                    nestedRows
                                                        ? () =>
                                                              setOpenRows((prev) => ({
                                                                  ...prev,
                                                                  [row.key]: !prev[row.key],
                                                              }))
                                                        : undefined
                                                }
                                            >
                                                {nestedRows ? (
                                                    <span className="fp-eligibility-change-modal__row-label">
                                                        {nestedOpen ? (
                                                            <CaretDownIcon size={14} weight="bold" />
                                                        ) : (
                                                            <CaretRightIcon size={14} weight="bold" />
                                                        )}
                                                        {row.label.replace(/ Eligibility$/, '')}
                                                    </span>
                                                ) : (
                                                    row.label.replace(/ Eligibility$/, '')
                                                )}
                                            </td>
                                            <td className="fp-eligibility-change-modal__current">
                                                {formatEligibilityCurrentValue(row.key, currentValues)}
                                            </td>
                                            {columns.map((column) => {
                                                const isCurrent = column.partner.id === currentPartnerId;
                                                const isRecommended = column.partner.id === recommendedId;
                                                return (
                                                    <td
                                                        key={`${column.partner.id}-${row.key}`}
                                                        className={`${isCurrent ? 'is-current' : ''} ${isRecommended ? 'is-recommended' : ''}`}
                                                    >
                                                        <StatusMark
                                                            status={column.statuses[row.key] ?? 'warning'}
                                                            reason={
                                                                getEligibilityRowInfo(
                                                                    row.key,
                                                                    column.partner,
                                                                    currentValues,
                                                                    column.statuses[row.key] ?? 'warning',
                                                                ).reason
                                                            }
                                                        />
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                        {nestedRows && nestedOpen
                                            ? nestedRows.map((nested) => (
                                                  <tr key={nested.key} className="fp-eligibility-change-modal__row--child">
                                                      <td>{nested.label}</td>
                                                      <td className="fp-eligibility-change-modal__current">
                                                          {formatEligibilityCurrentValue(nested.key, currentValues)}
                                                      </td>
                                                      {columns.map((column) => {
                                                          const isCurrent = column.partner.id === currentPartnerId;
                                                          const isRecommended = column.partner.id === recommendedId;
                                                          const status = column.statuses[nested.key] ?? 'warning';
                                                          return (
                                                              <td
                                                                  key={`${column.partner.id}-${nested.key}`}
                                                                  className={`${isCurrent ? 'is-current' : ''} ${isRecommended ? 'is-recommended' : ''}`}
                                                              >
                                                                  <StatusMark
                                                                      status={status}
                                                                      reason={
                                                                          getEligibilityRowInfo(
                                                                              nested.key,
                                                                              column.partner,
                                                                              currentValues,
                                                                              status,
                                                                          ).reason
                                                                      }
                                                                  />
                                                              </td>
                                                          );
                                                      })}
                                                  </tr>
                                              ))
                                            : null}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </EligibilityDetailsHost>
        </Modal>
    );
};

export default FinancePartnerChangeModal;
